import { Resume } from '@sr/types';
import { useEffect } from 'react';
import { Theme, ThemeProvider } from 'theme-ui';
import { DEFAULT_BASE_SIZE } from './common/base-theme';
import pageResume from './common/page-resume';
import { PagedProvider } from './common/paged-context';
import { Box } from './common/ui';
import { getResumeTemplateComponent } from './common/utils';

type Props = {
  resume: Resume;
  paged: boolean;
  localBaseSize?: number;
  forceDefaultBaseSize?: boolean;
  forceBreakpoint?: 'mobile' | 'desktop';
  webMinHeight?: string;
};

const MasterTemplate = ({
  resume,
  paged,
  localBaseSize,
  forceDefaultBaseSize,
  forceBreakpoint,
  webMinHeight,
}: Props) => {
  const { activeTemplateId, customizations } = resume.style;
  const activeCustomizations = customizations[activeTemplateId];

  if (!activeCustomizations) {
    throw new Error(`Missing activeCustomizations for resume: ${resume.id}`);
  }

  const ResumeTemplateComponent = getResumeTemplateComponent(activeTemplateId);
  const { theme } = ResumeTemplateComponent;

  // Override baseSize with localBaseSize if present
  if (localBaseSize) {
    activeCustomizations.baseSize = localBaseSize;
  }

  // Force default base size to ignore scaling
  if (forceDefaultBaseSize) {
    activeCustomizations.baseSize = DEFAULT_BASE_SIZE;
  }

  const customizedTheme = theme(activeCustomizations);

  // This causes all components to use styles from the SMALLEST breakpoint
  if (forceBreakpoint === 'mobile') {
    customizedTheme.breakpoints = ['5000px', '5001px', '5002px'];
  }

  // This causes all components to use styles from the LARGEST breakpoint
  if (forceBreakpoint === 'desktop') {
    customizedTheme.breakpoints = ['1px', '2px', '3px'];
  }

  const { web } = customizedTheme;
  useEffect(() => {
    if (paged) {
      pageResume({
        srcId: 'srcResume',
        destinationId: 'pdfResume',
        resumeTheme: customizedTheme,
      });
    }
  }, [resume, paged, customizedTheme]);
  return (
    <PagedProvider value={paged}>
      {/* This old version of theme-ui doesn't let you change the theme type */}
      <ThemeProvider theme={customizedTheme as Theme}>
        {paged ? (
          <>
            <Box id="srcResume">
              <ResumeTemplateComponent resume={resume} />
            </Box>
            <Box id="pdfResume" />
          </>
        ) : (
          <Box
            sx={{
              margin: '0 auto',
              width: '100%',
              maxWidth: web.maxWidth,
              backgroundColor: web.backgroundColor,
              overflow: 'hidden',
              minHeight: webMinHeight || 'auto',
            }}
          >
            <Box
              sx={{
                margin: '0 auto',
                width: '100%',
                maxWidth: web.contentWidth,
                paddingX: web.paddingX,
                paddingY: web.paddingY,
              }}
            >
              <ResumeTemplateComponent resume={resume} />
            </Box>
          </Box>
        )}
      </ThemeProvider>
    </PagedProvider>
  );
};

MasterTemplate.defaultProps = {
  paged: false,
};
export default MasterTemplate;
